import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { compareDaySequencesNewestFirst } from '@core/daySequence/daySequenceSort.util';
import {
  BreakType,
  CreateDaySequenceInput,
  DaySequence,
  DaySequenceChangeInput,
  Job,
} from '@core/generated/gql-types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import { canAllowManualTimesheetEntry } from '@core/util/manualTimesheetEntry.util';
import type { CheckoutTimesheetValues } from './CheckoutTimesheetModal';

/** Matches {@link JobDetailView} `query.daySequences.fetchWhen: 'tabSelected'`. */
export function useJobTimesheets(job: Job, isClientPortal: boolean) {
  const jobId = job.id;
  const approveDaySequences = useDaySequenceStore(
    (state) => state.approveDaySequences,
  );
  const resolveDaySequenceDisputes = useDaySequenceStore(
    (state) => state.resolveDaySequenceDisputes,
  );
  const proposeDaySequenceChanges = useDaySequenceStore(
    (state) => state.proposeDaySequenceChanges,
  );
  const clockAction = useDaySequenceStore((state) => state.clockAction);
  const createDaySequences = useDaySequenceStore(
    (state) => state.createDaySequences,
  );

  const [daySequences, setDaySequences] = useState<DaySequence[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [
    isUpdateTimesheetConfirmationOpen,
    setIsUpdateTimesheetConfirmationOpen,
  ] = useState(false);
  const [isEditTimesheetOpen, setIsEditTimesheetOpen] = useState(false);
  const [isCreateTimesheetOpen, setIsCreateTimesheetOpen] = useState(false);
  const [isCheckoutTimesheetOpen, setIsCheckoutTimesheetOpen] = useState(false);
  const [updateTimesheet, setUpdateTimesheet] = useState<DaySequence | null>(
    null,
  );
  const [editAction, setEditAction] = useState<'propose' | 'resolve'>(
    'resolve',
  );

  const fetchDaySequences = useCallback(
    async (force = false) => {
      if (!jobId) return;

      setIsLoading(true);
      try {
        const store = useDaySequenceStore.getState();

        if (force) {
          await store.fetchAllDaySequencesByJobId(jobId);
        } else {
          const existing = store.getDaySequenceByJobId(jobId);
          const meta = store.getDaySequencesMeta(jobId);

          if (existing.length === 0 && !meta?.fetchedAt) {
            await store.fetchAllDaySequencesByJobId(jobId);
          }
        }

        setDaySequences(
          useDaySequenceStore.getState().getDaySequenceByJobId(jobId),
        );
      } catch (error) {
        console.error('Error fetching day sequences for job:', error);
        setDaySequences([]);
      } finally {
        setIsLoading(false);
      }
    },
    [jobId],
  );

  useEffect(() => {
    void fetchDaySequences(true);
  }, [fetchDaySequences]);

  const handleApproveTimesheet = async (daySequenceId: string) => {
    try {
      setIsLoading(true);
      await approveDaySequences([daySequenceId]);
      toast.success('Timesheet approved');
    } catch (error) {
      console.error('Error approving day sequences:', error);
      toast.error('Error while approving day sequences');
    } finally {
      await fetchDaySequences(true);
      setIsLoading(false);
    }
  };

  /** Tenant: `resolveDaySequenceDisputes` */
  const handleResolveTimesheetDispute = async (
    daySequenceChange: DaySequenceChangeInput,
  ) => {
    try {
      setIsLoading(true);
      await resolveDaySequenceDisputes([daySequenceChange]).then(
        (processedDaySequences) => {
          if (processedDaySequences.length > 0) {
            toast.success('Timesheet dispute resolved');
          } else {
            toast.error('Failed to resolve timesheet dispute');
          }
        },
      );
    } catch (error) {
      console.error('Error resolving day sequence disputes:', error);
      toast.error('Error while resolving timesheet dispute');
    } finally {
      await fetchDaySequences(true);
      onCloseUpdateTimesheetConfirmation();
      setIsLoading(false);
    }
  };

  /** Client: `proposeDaySequenceChanges` */
  const handleProposeTimesheetChanges = async (
    daySequenceChange: DaySequenceChangeInput,
  ) => {
    try {
      setIsLoading(true);
      const processed = await proposeDaySequenceChanges([daySequenceChange]);
      if (processed.length > 0) {
        toast.success('Proposed times sent to candidate for confirmation');
      } else {
        toast.error('Failed to propose timesheet changes');
      }
    } catch (error) {
      console.error('Error proposing day sequence changes:', error);
      toast.error('Error while proposing timesheet changes');
    } finally {
      await fetchDaySequences(true);
      onCloseUpdateTimesheetConfirmation();
      onCloseEditTimesheet();
      setIsLoading(false);
    }
  };

  /**
   * Create manual timesheet when no row exists:
   * - Client → `clockAction`
   * - Tenant → `createDaySequences`
   */
  const handleCreateTimesheet = async (input: CreateDaySequenceInput) => {
    try {
      setIsLoading(true);

      if (isClientPortal) {
        const result = await clockAction(
          input.jobId,
          input.clockInTimestamp,
          input.clockOutTimestamp,
          input.breaks,
        );
        if (!result.success) {
          toast.error(result.error?.message ?? 'Failed to create timesheet');
          return;
        }
        toast.success('Timesheet created');
        return;
      }

      const created = await createDaySequences(input);
      if (created.length === 0) {
        toast.error('Failed to create timesheet');
        return;
      }
      toast.success('Timesheet created');
    } catch (error) {
      console.error('Error creating day sequences:', error);
      const errorMessage = error instanceof Error ? error.message : undefined;
      toast.error(errorMessage ?? 'Error while creating timesheet');
    } finally {
      await fetchDaySequences(true);
      setIsLoading(false);
    }
  };

  /** Worker checked in, not out → `clockAction` (client proposes checkout). */
  const handleCheckoutTimesheet = async (values: CheckoutTimesheetValues) => {
    if (!updateTimesheet?.clockInTime) {
      toast.error('Unable to find check-in time');
      return;
    }

    try {
      setIsLoading(true);
      const breaks =
        values.breakMinutes > 0
          ? [
              {
                daySequenceId: updateTimesheet.id,
                duration: String(minutesToMilliseconds(values.breakMinutes)),
                type: BreakType.Unpaid,
              },
            ]
          : undefined;

      const result = await clockAction(
        jobId,
        values.clockInTimestamp,
        values.clockOutTimestamp,
        breaks,
      );

      if (result.success) {
        toast.success(
          isClientPortal
            ? 'Proposed check-out sent to candidate for confirmation'
            : 'Check-out recorded',
        );
        onCloseCheckoutTimesheet();
      } else {
        toast.error(result.error?.message ?? 'Failed to check out');
      }
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error('Error while recording check-out');
    } finally {
      await fetchDaySequences(true);
      setIsLoading(false);
    }
  };

  const openUpdateTimesheetConfirmation = (timesheet: DaySequence) => {
    setIsUpdateTimesheetConfirmationOpen(true);
    setUpdateTimesheet(timesheet);
  };

  const openEditTimesheetModal = (
    daySequence: DaySequence,
    action: 'propose' | 'resolve',
  ) => {
    setEditAction(action);
    setIsEditTimesheetOpen(true);
    setUpdateTimesheet(daySequence);
  };

  const openCheckoutTimesheetModal = (daySequence: DaySequence) => {
    setUpdateTimesheet(daySequence);
    setIsCheckoutTimesheetOpen(true);
  };

  const onCloseUpdateTimesheetConfirmation = () => {
    setIsUpdateTimesheetConfirmationOpen(false);
    setUpdateTimesheet(null);
  };

  const onCloseEditTimesheet = () => {
    setIsEditTimesheetOpen(false);
    setUpdateTimesheet(null);
  };

  const onCloseCreateTimesheet = () => {
    setIsCreateTimesheetOpen(false);
  };

  const onCloseCheckoutTimesheet = () => {
    setIsCheckoutTimesheetOpen(false);
    setUpdateTimesheet(null);
  };

  const sortedDaySequences = useMemo(
    () => [...daySequences].sort(compareDaySequencesNewestFirst),
    [daySequences],
  );

  const canCreateTimesheet = useMemo(
    () =>
      canAllowManualTimesheetEntry(job, daySequences, {
        requireFilled: true,
        portal: 'tenant',
      }),
    [job, daySequences],
  );

  return {
    daySequences,
    isLoading,
    sortedDaySequences,
    canCreateTimesheet,
    isClientPortal,
    isUpdateTimesheetConfirmationOpen,
    isEditTimesheetOpen,
    isCreateTimesheetOpen,
    isCheckoutTimesheetOpen,
    setIsCreateTimesheetOpen,
    updateTimesheet,
    editAction,
    handleApproveTimesheet,
    handleResolveTimesheetDispute,
    handleProposeTimesheetChanges,
    handleCreateTimesheet,
    handleCheckoutTimesheet,
    openUpdateTimesheetConfirmation,
    openEditTimesheetModal,
    openCheckoutTimesheetModal,
    onCloseUpdateTimesheetConfirmation,
    onCloseEditTimesheet,
    onCloseCreateTimesheet,
    onCloseCheckoutTimesheet,
  };
}
